/**
 * P1-10~14: toolbar 指示器族。
 *
 * tooltip 内容生成函数逐字移植自 js/jsx/toolbar.jsx.js（仅把 this.game hack
 * 改为显式 game 参数）；显示/隐藏条件与旧版一致（见 INVENTORY.md）。
 * 数据订阅用 useGameTick 精确选择器——只有显示值变化才重渲染。
 */
import { useEffect, useRef, useState } from "react";
import { IconStat } from "../../components/IconStat";
import { getGame } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import type { GamePage } from "../../bridge/GameBridge";

type Eq<T> = (a: T, b: T) => boolean;

/** 可空对象字段级相等（选择器缓存用） */
function nullableFieldEq<T extends Record<string, unknown>>(a: T | null, b: T | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) {
    return false;
  }
  return ka.every((k) => Object.is(a[k], b[k]));
}

// ---------------------------------------------------------------------------
// P1-10 Happiness
// ---------------------------------------------------------------------------

function happinessTooltip(game: GamePage): string {
  const base = game.getEffect("happiness");
  const challengeHappiness = game.getEffect("challengeHappiness");
  // "base" is usually 100%, but it gets reduced in certain Challenges
  let tooltip = t("village.happiness.base") + ": " + game.getDisplayValueExt(100 + challengeHappiness, false, false, 0) + "%<br>";
  tooltip += t("village.happiness.buildings") + ": +" + Math.floor(base) + "%<br>";

  //----------------------
  let resHappiness = 0;
  const resources = game.resPool.resources;
  let happinessPerLuxury = 10;
  //philosophy epicurianism effect
  happinessPerLuxury += game.getEffect("luxuryHappinessBonus");
  for (let i = resources.length - 1; i >= 0; i--) {
    if (resources[i].type != "common" && resources[i].value > 0) {
      resHappiness += happinessPerLuxury;
      if (resources[i].name == "elderBox" && game.resPool.get("wrappingPaper").value) {
        resHappiness -= happinessPerLuxury; // Present Boxes and Wrapping Paper do not stack.
      }
      if (resources[i].type == "uncommon") {
        resHappiness += game.getEffect("consumableLuxuryHappiness");
      }
    }
  }
  tooltip += t("village.happiness.rare.resources") + ": +" + game.getDisplayValueExt(resHappiness, false, false, 0) + "%<br>";
  //---------------------
  const karma = game.village.getHappinessFromKarma();
  if (karma > 0) {
    tooltip += t("village.happiness.karma") + ": +" + game.getDisplayValueExt(karma, false, false, 0) + "%<br>";
  }

  if (game.calendar.festivalDays > 0) {
    const festivalHappinessEffect = 30 * (1 + game.getEffect("festivalRatio"));
    tooltip += t("village.happiness.festival") + ": +" + game.getDisplayValueExt(festivalHappinessEffect, false, false, 0) + "%<br>";
  }

  const unhappiness = game.village.getUnhappiness() / (1 + game.getEffect("unhappinessRatio"));
  const unhappinessReduction = unhappiness * game.getEffect("unhappinessRatio", true);
  const environmentEffect = game.village.getEnvironmentEffect();
  tooltip += t("village.happiness.penalty") + ": -" + game.getDisplayValueExt(unhappiness + unhappinessReduction, false, false, 0) + "%<br>";

  tooltip += "* " + t("village.happiness.penalty.base") + ": -" + game.getDisplayValueExt(unhappiness, false, false, 0) + "%<br>";
  tooltip += "* " + t("village.happiness.penalty.mitigated") + ": " + game.getDisplayValueExt(-unhappinessReduction, false, false, 0) + "%<br>";
  tooltip += t("village.happiness.environment") + ": " + game.getDisplayValueExt(environmentEffect, false, false, 0) + "%<br>";
  const overpopulation = game.village.getOverpopulation();
  if (overpopulation > 0) {
    tooltip += t("village.happiness.overpopulation") + ": -" + overpopulation * 2 + "%<br>";
  }

  return tooltip;
}

export function ToolbarHappiness() {
  const value = useGameTick((game) => {
    if (!game || game.village.getKittens() <= 5) {
      return null;
    }
    return Math.floor(game.village.happiness * 100);
  });

  if (value === null) {
    return null;
  }
  return (
    <IconStat className="happiness" getTooltip={() => happinessTooltip(getGame() as GamePage)}>
      <div className="happinessText">{value}%</div>
    </IconStat>
  );
}

// ---------------------------------------------------------------------------
// P1-11 Energy
// ---------------------------------------------------------------------------

function energyTooltip(game: GamePage): string {
  const resPool = game.resPool;
  const energy = resPool.energyProd - resPool.energyCons;

  const delta = game.resPool.getEnergyDelta();
  const penalty = energy >= 0 ? "" : "<br><br>" + t("navbar.energy.penalty") + "<span class='energyPenalty'>-" + Math.floor((1 - delta) * 100) + "%</span>";

  return t("navbar.energy.prod.short") + " <span class='energyProduction'>" + game.getDisplayValueExt(resPool.energyProd, true, false) + t("unit.watt") + "</span>" +
    "<br>" + t("navbar.energy.cons.short") + " <span class='energyConsumption'>-" + game.getDisplayValueExt(resPool.energyCons) + t("unit.watt") + "</span>" + penalty;
}

interface EnergyState {
  text: string;
  cls: string;
}

export function ToolbarEnergy() {
  const state = useGameTick<EnergyState | null>((game) => {
    if (!game || !game.science.get("electricity").researched) {
      return null;
    }
    const resPool = game.resPool;
    let cls = "";
    if (resPool.energyProd < resPool.energyCons) {
      cls = "warning";
    } else if (resPool.energyWinterProd < resPool.energyCons) {
      cls = "warningWinter";
    }
    return {
      text: game.getDisplayValueExt(resPool.energyProd - resPool.energyCons) + t("unit.watt"),
      cls,
    };
  }, nullableFieldEq as Eq<EnergyState | null>);

  if (!state) {
    return null;
  }
  return (
    <IconStat className={`energy ${state.cls}`.trim()} getTooltip={() => energyTooltip(getGame() as GamePage)}>
      <div className="energyText">{state.text}</div>
    </IconStat>
  );
}

// ---------------------------------------------------------------------------
// P1-12 MOTD（与旧版一致：组件保留但不挂载，toolbar.jsx.js:757）
// ---------------------------------------------------------------------------

function motdTooltip(game: GamePage): string {
  const server = game.server;
  if (server.showMotd && server.motdContent) {
    server.motdFreshMessage = false;
    return "Message of the day:<br />" + server.motdContent;
  }
  return "";
}

export function ToolbarMOTD() {
  const state = useGameTick<{ title: string; fresh: boolean } | null>((game) => {
    if (!game || !game.server.showMotd || !game.server.motdTitle) {
      return null;
    }
    return { title: game.server.motdTitle, fresh: !!game.server.motdFreshMessage };
  }, nullableFieldEq as Eq<{ title: string; fresh: boolean } | null>);

  if (!state) {
    return null;
  }
  return (
    <IconStat className={state.fresh ? "freshMessage" : ""} getTooltip={() => motdTooltip(getGame() as GamePage)}>
      <div>&nbsp;{state.title}&nbsp;</div>
    </IconStat>
  );
}

// ---------------------------------------------------------------------------
// P1-13 Pollution
// ---------------------------------------------------------------------------

function pollutionMod(game: GamePage): string {
  return game.getDisplayValueExt((game.bld.cathPollution / game.bld.getPollutionLevelBase()) * 100) + "ppm";
}

/** 旧 getTooltip 的 message 主体（等级 + warning + 趋势），不含 CO₂ 行 */
function pollutionMessage(game: GamePage): string {
  let message = "";
  const eqPol = game.bld.getEquilibriumPollution();
  const eqPolLvl = game.bld.getPollutionLevel(eqPol);
  const pollution = game.bld.cathPollution;
  const polLvl = game.bld.getPollutionLevel();
  const polLvlShow = game.bld.getPollutionLevel(pollution * 2);
  if (polLvl >= 4) {
    message += t("pollution.level1") + "<br/>" + t("pollution.level2") + "<br/>" + t("pollution.level3", [game.villageTab.getVillageTitle()]) + "<br/>" + t("pollution.level4");
  } else if (polLvlShow == 3 || polLvl == 3) {
    message += t("pollution.level1") + "<br/>" + t("pollution.level2") + "<br/>" + t("pollution.level3", [game.villageTab.getVillageTitle()]);
  } else if (polLvlShow == 2) {
    message += t("pollution.level1") + "<br/>" + t("pollution.level2");
  } else if (polLvlShow == 1) {
    message += t("pollution.level1");
  } else {
    message = t("pollution.level0");
  }

  const warnLvl = game.bld.getPollutionLevel(pollution * 4);
  if (warnLvl >= 1 && warnLvl <= 4 && warnLvl > polLvlShow && warnLvl <= eqPolLvl) {
    message += "<br/>" + t("pollution.level" + warnLvl + ".warning");
  }
  if (pollution * 1.5 <= eqPol || eqPolLvl > polLvl) {
    message += "<br/>" + t("pollution.increasing");
  } else if (pollution >= 0 && game.bld.cathPollutionPerTick <= 0 && eqPolLvl < polLvl) {
    message += "<br/>" + t("pollution.cleaning");
  } else if (eqPolLvl == polLvl && eqPol > 0) {
    message += "<br/>" + t("pollution.equilibrium");
  } else {
    message += "<br/>" + t("pollution.pristine");
  }
  return message;
}

function pollutionTooltip(game: GamePage): string {
  return pollutionMessage(game) + "<br/>CO₂: " + (game.science.get("ecology").researched ? pollutionMod(game) : t("pollution.unspecified"));
}

interface PollutionState {
  visible: boolean;
  message: string;
  mod: string;
}

export function ToolbarPollution() {
  // 注意：message 必须像旧版一样在不可见时也持续跟踪（toolbar.jsx.js:183 的
  // getTooltip(true) 先于可见性判断执行），否则从不可见跳到可见时
  // prevMessage 为空、freshMessage 永远点不亮。
  const state = useGameTick<PollutionState | null>((game) => {
    if (!game) {
      return null;
    }
    const researched = game.science.get("ecology").researched;
    return {
      visible: game.bld.cathPollution > 100000 || researched,
      message: pollutionMessage(game),
      mod: researched ? pollutionMod(game) : " ",
    };
  }, nullableFieldEq as Eq<PollutionState | null>);

  // 旧 freshMessage 语义（toolbar.jsx.js:183-187）：message 变化时点亮脉冲，
  // tooltip 被查看后熄灭（旧版在 getTooltip 内复位，此处等价）。
  const [fresh, setFresh] = useState(false);
  const prevMessageRef = useRef("");
  useEffect(() => {
    if (state && prevMessageRef.current !== state.message) {
      setFresh(prevMessageRef.current !== "");
      prevMessageRef.current = state.message;
    }
  }, [state]);

  if (!state || !state.visible) {
    return null;
  }
  return (
    <IconStat
      className={`pollutionIcon ${fresh ? "energy warning" : ""}`.trim()}
      getTooltip={() => {
        setFresh(false);
        return pollutionTooltip(getGame() as GamePage);
      }}
    >
      <div className="pollutionText">{state.mod}</div>
    </IconStat>
  );
}

// ---------------------------------------------------------------------------
// P1-14 FPS 与 BLS
// ---------------------------------------------------------------------------

function fpsTooltip(game: GamePage): string {
  const fps = game.fps;
  return " avg: " + fps.avg.toFixed() +
    " ms [" + fps.avg0.toFixed() +
    "." + fps.avg1.toFixed() +
    "." + fps.avg2.toFixed() +
    "." + fps.avg3.toFixed() +
    "." + fps.avg4.toFixed() + "] (Cl. to res.)";
}

export function ToolbarFps() {
  const ms = useGameTick((game) => (game && game.isLocalhost ? game.fps.ms : null));

  if (ms === null) {
    return null;
  }
  return (
    <IconStat getTooltip={() => fpsTooltip(getGame() as GamePage)}>
      <div>fps: {ms} ms</div>
    </IconStat>
  );
}

interface BlsState {
  text: string;
  isMax: boolean;
}

export function ToolbarBls() {
  const state = useGameTick<BlsState | null>((game) => {
    if (!game) {
      return null;
    }
    const sorrowRes = game.resPool.get("sorrow");
    const sorrow = sorrowRes.value;
    if (!sorrow) {
      return null;
    }
    return {
      text: t("resources.sorrow.short") + ": " + sorrow.toFixed() + "%",
      isMax: sorrowRes.value == sorrowRes.maxValue,
    };
  }, nullableFieldEq as Eq<BlsState | null>);

  if (!state) {
    return null;
  }
  return (
    <IconStat
      className={`sorrow ${state.isMax ? "max" : ""}`.trim()}
      getTooltip={() => t("resources.sorrow.full")}
    >
      <div>{state.text}</div>
    </IconStat>
  );
}
